import { useMemo, useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import { ActionBar, DataGrid, StatusBar, type GridColumn } from '@/components/desktop';
import { ReverseButton, printDocument } from '@/components/documents';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  OkNote,
  Pager,
  ScreenWithForm,
  SearchBar,
  Select,
  StatusPill,
  Table,
  TextArea,
  errorMessage,
} from '@/components/ui';
import { formatCount, formatDate, formatMoney, formatQuantity, roundMoney } from '@/format';
import {
  DEDUCTION_LABELS,
  useBillableDispatches,
  useBills,
  useFinanceMutation,
  useLastRate,
  type BillRow,
} from '@/hooks/finance';
import { useBuyers } from '@/hooks/sales';
import { useAuth } from '@/stores/auth';
import { useFarmScope } from '@/stores/farmScope';

const PAGE = 50;

const STATUSES = [
  { value: '', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'POSTED', label: 'Posted' },
  { value: 'REVERSED', label: 'Reversed' },
];

const KINDS = Object.entries(DEDUCTION_LABELS).map(([value, label]) => ({ value, label }));

interface DeductionDraft {
  kind: string;
  label: string;
  amount: string;
}

/**
 * Financials › Dispatch Billing.
 *
 * Net weight times rate is the gross; deductions come off it to give what the
 * buyer owes. Each deduction is a line with a kind rather than one lump, because
 * commission is the farm's selling cost, weight shortage is an allowance against
 * what was sold, and freight is cartage — three different places in the accounts,
 * and a lump nobody has classified cannot be posted at all.
 *
 * The gross and the net are what `fin.margin.read` guards, so for a role without
 * it the API leaves both out and this screen shows a dash. That is the intended
 * behaviour, not a bug: a bill is a margin document.
 */
export function Billing() {
  const can = useAuth((s) => s.can);
  const farmIds = useFarmScope((s) => s.selectedFarmIds);

  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<BillRow | null>(null);
  const [gatePassId, setGatePassId] = useState('');
  const [ratePerKg, setRatePerKg] = useState('');
  const [billDate, setBillDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [deductions, setDeductions] = useState<DeductionDraft[]>([]);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const bills = useBills({ farmIds, status, search, take: PAGE, skip });
  const billable = useBillableDispatches(farmIds);
  const buyers = useBuyers();
  const mutate = useFinanceMutation<unknown>();

  const rows = bills.data?.items ?? [];
  const total = bills.data?.total ?? 0;
  const current = rows.find((r) => r.id === selected) ?? null;

  const pass = (billable.data?.items ?? []).find((d) => d.id === gatePassId) ?? null;
  const lastRate = useLastRate(pass?.buyerId ?? '');

  const netKg = editing ? editing.netKg : (pass?.netKg ?? null);
  const figures = useMemo(() => {
    const kg = Number(netKg ?? 0);
    const rate = Number(ratePerKg);
    if (!Number.isFinite(kg) || !Number.isFinite(rate) || ratePerKg === '') return null;
    const gross = roundMoney(kg * rate);
    // Rounded so 0.1 + 0.2 is 0.30 and not "more than" a 0.30 bill.
    const off = roundMoney(
      deductions.reduce((sum, d) => {
        const n = Number(d.amount);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0),
    );
    return {
      gross: gross.toFixed(2),
      deductions: off.toFixed(2),
      net: roundMoney(gross - off).toFixed(2),
      overDeducted: off > gross,
    };
  }, [netKg, ratePerKg, deductions]);

  function startAdd() {
    setEditing(null);
    setGatePassId('');
    setRatePerKg('');
    setBillDate('');
    setRemarks('');
    setDeductions([]);
    setError('');
    setNote('');
    setShowForm(true);
  }

  function startEdit(row: BillRow) {
    setEditing(row);
    setGatePassId(row.gatePassId ?? '');
    setRatePerKg(row.ratePerKg);
    setBillDate(row.billDate);
    setRemarks(row.remarks ?? '');
    setDeductions(
      row.deductionLines.map((l) => ({
        kind: l.kind,
        label: l.label ?? '',
        amount: l.amount,
      })),
    );
    setError('');
    setNote('');
    setShowForm(true);
  }

  async function save() {
    setError('');
    setNote('');
    if (!editing && !gatePassId) {
      setError('Pick the dispatch this bill is for');
      return;
    }
    if (!ratePerKg || Number(ratePerKg) < 0) {
      setError('Enter the rate per kilogram');
      return;
    }
    if (figures?.overDeducted) {
      setError('The deductions add up to more than the bill');
      return;
    }

    const lines = deductions
      .filter((d) => d.amount.trim() !== '' && Number(d.amount) > 0)
      .map((d) => ({
        kind: d.kind,
        ...(d.label.trim() ? { label: d.label.trim() } : {}),
        amount: d.amount,
      }));

    try {
      if (editing) {
        await mutate.mutateAsync(() =>
          api.patch(`/api/v1/fin/bills/${editing.id}`, {
            version: editing.version,
            ratePerKg,
            ...(billDate ? { billDate } : {}),
            deductions: lines,
            remarks,
          }),
        );
      } else {
        await mutate.mutateAsync(() =>
          api.post('/api/v1/fin/bills', {
            gatePassId,
            ratePerKg,
            ...(billDate ? { billDate } : {}),
            ...(lines.length > 0 ? { deductions: lines } : {}),
            ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
          }),
        );
      }
      setNote(editing ? 'Bill saved.' : 'Bill raised.');
      setShowForm(false);
      setEditing(null);
    } catch (e) {
      setError(errorMessage(e, 'That bill could not be saved'));
    }
  }

  async function post(row: BillRow) {
    setError('');
    setNote('');
    try {
      await mutate.mutateAsync(() =>
        api.post(`/api/v1/fin/bills/${row.id}/post`, { version: row.version }),
      );
      setNote(`${row.documentNo} posted.`);
    } catch (e) {
      // The credit-limit refusal lands here, and it already says what to do about it.
      setError(errorMessage(e, 'That bill could not be posted'));
    }
  }

  async function remove(row: BillRow) {
    setError('');
    try {
      await mutate.mutateAsync(() =>
        api.delete(`/api/v1/fin/bills/${row.id}?version=${row.version}`),
      );
      setNote('Draft deleted.');
      setSelected(null);
    } catch (e) {
      setError(errorMessage(e, 'That draft could not be deleted'));
    }
  }

  function print(row: BillRow) {
    printDocument({
      title: `Bill ${row.documentNo}`,
      facts: [
        ['Bill no', row.documentNo],
        ['Date', formatDate(row.billDate)],
        ['Party', row.buyerName],
        ['Farm', row.farmName],
        ['Gate pass no', row.bookGatePassNo ?? row.gatePassNo ?? '—'],
        ['Birds', row.birdCount === null ? '—' : formatCount(row.birdCount)],
        ['Net weight', `${row.netKg} kg`],
        ['Rate per kg', formatMoney(row.ratePerKg)],
        ['Gross', row.grossAmount === undefined ? '—' : formatMoney(row.grossAmount)],
        ...row.deductionLines.map(
          (l) =>
            [
              `Less ${l.label ?? DEDUCTION_LABELS[l.kind] ?? l.kind}`,
              formatMoney(l.amount),
            ] as [string, string],
        ),
        ['Net payable', row.netAmount === undefined ? '—' : formatMoney(row.netAmount)],
        ['Received so far', formatMoney(row.paidAmount)],
        ['Outstanding', formatMoney(row.outstanding)],
      ],
      showCost: true,
    });
    void api.get(`/api/v1/fin/bills/{id}/print`.replace('{id}', row.id) as never);
  }

  const columns: Array<GridColumn<BillRow>> = [
    { key: 'billDate', header: 'Date', width: 104, render: (r) => formatDate(r.billDate) },
    { key: 'documentNo', header: 'Bill no', width: 140, render: (r) => r.documentNo },
    {
      key: 'bookGatePassNo',
      header: 'Gate pass no',
      width: 110,
      render: (r) => r.bookGatePassNo ?? r.gatePassNo ?? <span className="muted">—</span>,
    },
    { key: 'buyerName', header: 'Party', width: 170, render: (r) => r.buyerName },
    { key: 'farmName', header: 'Farm', width: 130, render: (r) => r.farmName },
    {
      key: 'netKg',
      header: 'Net weight',
      width: 110,
      align: 'end',
      render: (r) => formatQuantity(r.netKg, 3),
    },
    {
      key: 'ratePerKg',
      header: 'Rate',
      width: 90,
      align: 'end',
      render: (r) => formatMoney(r.ratePerKg),
    },
    {
      key: 'grossAmount',
      header: 'Gross',
      width: 120,
      align: 'end',
      // Absent rather than zero for a role without fin.margin.read — the server
      // never sent it, which is the point of a field permission.
      render: (r) =>
        r.grossAmount === undefined ? <span className="muted">—</span> : formatMoney(r.grossAmount),
    },
    {
      key: 'deductions',
      header: 'Deductions',
      width: 110,
      align: 'end',
      render: (r) => formatMoney(r.deductions),
    },
    {
      key: 'netAmount',
      header: 'Net',
      width: 120,
      align: 'end',
      render: (r) =>
        r.netAmount === undefined ? <span className="muted">—</span> : formatMoney(r.netAmount),
    },
    {
      key: 'outstanding',
      header: 'Outstanding',
      width: 120,
      align: 'end',
      render: (r) => formatMoney(r.outstanding),
    },
    { key: 'status', header: 'Status', width: 120, render: (r) => <StatusPill status={r.status} /> },
  ];

  const form = (
    <FormPanel
      title={editing ? `Bill ${editing.documentNo}` : 'Raise a bill'}
      icon={<AppIcon kind="cash" size={18} />}
      intro="Pick a posted dispatch and put a rate on it. Deductions come off the gross."
    >
      <FormGroup title="What is being billed" columns={2}>
        {editing ? (
          <Field
            label="Dispatch"
            value={`${editing.bookGatePassNo ?? editing.gatePassNo ?? '—'} · ${editing.buyerName}`}
            onChange={() => undefined}
            disabled
          />
        ) : (
          <Select
            label="Dispatch"
            value={gatePassId}
            onChange={setGatePassId}
            options={(billable.data?.items ?? []).map((d) => ({
              value: d.id,
              label: `${d.bookGatePassNo ?? d.documentNo} · ${d.buyerName} · ${d.netKg ?? '—'} kg`,
            }))}
            required
            hint={
              (billable.data?.items.length ?? 0) === 0
                ? 'Nothing to bill: every posted dispatch already has a bill on it.'
                : 'Only posted dispatches with no bill on them appear here.'
            }
          />
        )}
        <Field
          label="Net weight"
          value={netKg ?? ''}
          onChange={() => undefined}
          disabled
          mono
          hint="From the dispatch. A bill cannot change what was weighed."
        />
        <Field
          label="Rate per kg"
          type="number"
          value={ratePerKg}
          onChange={setRatePerKg}
          required
          hint={
            lastRate.data?.ratePerKg
              ? `Last billed at ${formatMoney(lastRate.data.ratePerKg)} on ${formatDate(lastRate.data.billDate)}`
              : 'There is no rate master; type what was agreed.'
          }
        />
        <Field
          label="Bill date"
          type="date"
          value={billDate}
          onChange={setBillDate}
          hint="Leave blank to use the dispatch's own date."
        />
      </FormGroup>

      <FormGroup
        title="Deductions"
        hint="Each one needs a kind, so it can reach the account it belongs on."
      >
        <Table
          count={deductions.length}
          empty="No deductions. Add one if something is coming off this bill."
          head={
            <tr>
              <th>What for</th>
              <th>Note</th>
              <th className="num">Amount</th>
              <th />
            </tr>
          }
        >
          {deductions.map((d, i) => (
            <tr key={i}>
              <td>
                <Select
                  label=""
                  value={d.kind}
                  onChange={(v) =>
                    setDeductions((list) =>
                      list.map((x, j) => (i === j ? { ...x, kind: v } : x)),
                    )
                  }
                  options={KINDS}
                />
              </td>
              <td>
                <Field
                  label=""
                  value={d.label}
                  onChange={(v) =>
                    setDeductions((list) =>
                      list.map((x, j) => (i === j ? { ...x, label: v } : x)),
                    )
                  }
                  placeholder="Commission at Rs 2/kg"
                />
              </td>
              <td className="num">
                <Field
                  label=""
                  type="number"
                  value={d.amount}
                  onChange={(v) =>
                    setDeductions((list) =>
                      list.map((x, j) => (i === j ? { ...x, amount: v } : x)),
                    )
                  }
                />
              </td>
              <td>
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  onClick={() => setDeductions((list) => list.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </Table>
        <button
          type="button"
          className="btn btn--sm"
          onClick={() =>
            setDeductions((list) => [...list, { kind: 'COMMISSION', label: '', amount: '' }])
          }
        >
          Add a deduction
        </button>
      </FormGroup>

      <FormGroup title="What it comes to" columns={3}>
        <Field
          label="Gross"
          value={figures?.gross ?? ''}
          onChange={() => undefined}
          disabled
          mono
          hint="Net weight times rate."
        />
        <Field
          label="Deductions"
          value={figures?.deductions ?? ''}
          onChange={() => undefined}
          disabled
          mono
        />
        <Field
          label="Net payable"
          value={figures?.net ?? ''}
          onChange={() => undefined}
          disabled
          mono
          hint={figures?.overDeducted ? 'The deductions are more than the bill' : undefined}
        />
      </FormGroup>

      <FormGroup title="Note">
        <TextArea label="Note" value={remarks} onChange={setRemarks} />
      </FormGroup>

      {error && <ErrorNote message={error} />}

      <FormActions onCancel={() => setShowForm(false)}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={mutate.isPending}
          onClick={() => void save()}
        >
          {editing ? 'Save changes' : 'Save bill'}
        </button>
      </FormActions>
    </FormPanel>
  );

  return (
    <ScreenWithForm
      canCreate={can('fin.bill.create')}
      addLabel="Raise a bill"
      showForm={showForm}
      onToggle={() => (showForm ? setShowForm(false) : startAdd())}
      form={form}
    >
      <div className="toolbar toolbar--wrap">
        <SearchBar
          value={search}
          onChange={(v) => {
            setSearch(v);
            setSkip(0);
          }}
          placeholder="Search bill no, gate pass or party"
        />
        <Select
          label="Status"
          value={status}
          onChange={(v) => {
            setStatus(v);
            setSkip(0);
          }}
          options={STATUSES}
          placeholder="All"
        />
      </div>

      {note && <OkNote message={note} />}
      {error && !showForm && <ErrorNote message={error} />}

      <ActionBar
        actions={[
          {
            label: 'Edit',
            icon: 'edit',
            enabled: Boolean(current && current.status === 'DRAFT' && can('fin.bill.update')),
            reason: 'Only a draft can be edited. A posted bill is corrected by reversing it.',
            onSelect: () => current && startEdit(current),
          },
          {
            label: 'Post',
            icon: 'post',
            primary: true,
            enabled: Boolean(current && current.status === 'DRAFT' && can('fin.bill.post')),
            reason: 'Only a draft can be posted',
            onSelect: () => current && void post(current),
          },
          {
            label: 'Print',
            icon: 'print',
            enabled: Boolean(current) && can('fin.bill.print'),
            reason: 'Pick a bill first',
            onSelect: () => current && print(current),
          },
          {
            label: 'Delete draft',
            icon: 'remove',
            danger: true,
            enabled: Boolean(current && current.status === 'DRAFT' && can('fin.bill.delete')),
            reason: 'Only a draft can be deleted',
            onSelect: () => current && void remove(current),
          },
        ]}
      >
        {current?.status === 'POSTED' && can('fin.bill.reverse') && (
          <ReverseButton
            label={`Reverse ${current.documentNo}`}
            pending={mutate.isPending}
            onReverse={(reason) => {
              void mutate
                .mutateAsync(() =>
                  api.post(
                    `/api/v1/fin/bills/${current.id}/reverse?version=${current.version}`,
                    { reason },
                  ),
                )
                .then(() => setNote('Bill reversed.'))
                .catch((e: unknown) =>
                  // Refused while a receipt is applied — the message names the receipt.
                  setError(errorMessage(e, 'That bill could not be reversed')),
                );
            }}
          />
        )}
      </ActionBar>

      <DataGrid
        id="fin-bills"
        label="Dispatch bills"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        selectedKey={selected}
        onSelect={(key) => setSelected(key)}
        onOpen={(row) => row.status === 'DRAFT' && startEdit(row)}
        empty="No bills yet."
        loading={bills.isLoading}
      />

      <Pager skip={skip} take={PAGE} total={total} onChange={setSkip} />

      <StatusBar>
        <span>
          {total} bill{total === 1 ? '' : 's'}
        </span>
        {buyers.data && <span>{buyers.data.items.length} buyers on file</span>}
      </StatusBar>
    </ScreenWithForm>
  );
}
